import React from 'react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import { GroupedField } from './SectionGroupedFields';
import DemographicsSummaryField from '../../../models/serviceModels/DemographicsSummaryField';

interface DataVisualizationProps {
    data: GroupedField<DemographicsSummaryField>;
}
// Define the structure of the chart data
interface ChartData {
    fieldLabel: string;
    fieldGrouping?: string | null;
    totalValue?: number;
    percentageValue?: number;
}

const MAX_TICK_LINE_LENGTH = 12;
const MAX_TICK_LINES = 3;
const X_AXIS_HEIGHT = 88;

const splitLongWord = (word: string, maxLength: number) => {
    const chunks: string[] = [];

    for (let i = 0; i < word.length; i += maxLength) {
        chunks.push(word.slice(i, i + maxLength));
    }

    return chunks;
};

const wrapTickLabel = (label: string, maxLength: number, maxLines: number) => {
    const words = label.split(/\s+/).flatMap((word) => {
        if (word.length <= maxLength) {
            return [word];
        }

        return splitLongWord(word, maxLength);
    });

    const lines: string[] = [];
    let currentLine = '';

    words.forEach((word) => {
        const nextLine = currentLine ? `${currentLine} ${word}` : word;
        if (nextLine.length <= maxLength) {
            currentLine = nextLine;
            return;
        }

        if (currentLine) {
            lines.push(currentLine);
        }
        currentLine = word;
    });

    if (currentLine) {
        lines.push(currentLine);
    }

    if (lines.length <= maxLines) {
        return lines;
    }

    const truncated = lines.slice(0, maxLines);
    const lastLine = truncated[maxLines - 1];
    truncated[maxLines - 1] =
        lastLine.length >= maxLength ? `${lastLine.slice(0, maxLength - 3)}...` : `${lastLine}...`;
    return truncated;
};

const WrappedTick = ({ x = 0, y = 0, payload }: any) => {
    const lines = wrapTickLabel(String(payload?.value || ''), MAX_TICK_LINE_LENGTH, MAX_TICK_LINES);

    return (
        <g transform={`translate(${x},${y})`}>
            <text
                x={0}
                y={0}
                dy={16}
                textAnchor="middle"
                fill="#666"
                fontSize={12}
            >
                {lines.map((line, index) => (
                    <tspan key={`${line}-${index}`} x={0} dy={index === 0 ? 0 : 14}>
                        {line}
                    </tspan>
                ))}
            </text>
        </g>
    );
};

const DataVisualization: React.FC<DataVisualizationProps> = ({ data }) => {
    // Prepare the chart data
    const chartData: ChartData[] = data.fields.map((field) => ({
        fieldLabel: field.fieldLabel,
        fieldGrouping: field.fieldGrouping,
        totalValue: field.totalValue,
    }));

    return (
        <div style={{ marginBottom: '40px' }}>
            <h2>{data.label}</h2>
            <ResponsiveContainer width="100%" height={400}>
                <BarChart
                    data={chartData}
                    margin={{
                        top: 20, right: 30, left: 20, bottom: 20,
                    }}
                >
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis
                        dataKey="fieldGrouping"
                        interval={0}
                        height={X_AXIS_HEIGHT}
                        tick={<WrappedTick />}
                    />
                    <YAxis />
                    <Tooltip />
                    <Legend />
                    <Bar dataKey="totalValue" fill="#8884d8" name="Total" />
                </BarChart>
            </ResponsiveContainer>
        </div>
    );
};

export default DataVisualization;
